/**
 * Generated by orval v7.17.0 🍺
 * Do not edit manually.
 * FieldPay API
 * FieldPay API for managing organizations, opportunities, payouts and talent management
 * OpenAPI spec version: v1
 */
import { useMutation, useQuery } from "@tanstack/react-query";
import type {
  MutationFunction,
  QueryFunction,
  QueryKey,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
} from "@tanstack/react-query";

import * as axios from "axios";
import type { AxiosError, AxiosRequestConfig, AxiosResponse } from "axios";

import type { CreateOpportunityDto } from "../types/createOpportunityDto";

import type { CreateOpportunityFromTemplateDto } from "../types/createOpportunityFromTemplateDto";

import type { GetOpportunityClaimsParams } from "../types/getOpportunityClaimsParams";

import type { GetOpportunitySharesParams } from "../types/getOpportunitySharesParams";

import type { InviteTalentMembersDto } from "../types/inviteTalentMembersDto";

import type { ListOpportunitiesParams } from "../types/listOpportunitiesParams";

import type { OpportunityClaimDtoPaginatedResult } from "../types/opportunityClaimDtoPaginatedResult";

import type { OpportunityDto } from "../types/opportunityDto";

import type { OpportunityDtoPaginatedResult } from "../types/opportunityDtoPaginatedResult";

import type { OpportunityShareDtoPaginatedResult } from "../types/opportunityShareDtoPaginatedResult";

import type { OpportunityStatsDto } from "../types/opportunityStatsDto";

import type { PatchOpportunityDto } from "../types/patchOpportunityDto";

import type { ProblemDetails } from "../types/problemDetails";

import type { RemoveSharesDto } from "../types/removeSharesDto";

import type { UpdateOpportunityDto } from "../types/updateOpportunityDto";

type AwaitedInput<T> = PromiseLike<T> | T;

type Awaited<O> = O extends AwaitedInput<infer T> ? T : never;

/**
 * **Required Permission(s):** ORG_OPP_LIST
 */
export const listOpportunities = (
  params?: ListOpportunitiesParams,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<OpportunityDtoPaginatedResult>> => {
  return axios.default.get(`/api/opportunities`, {
    ...options,
    params: { ...params, ...options?.params },
  });
};

export const getListOpportunitiesQueryKey = (
  params?: ListOpportunitiesParams,
) => {
  return [`/api/opportunities`, ...(params ? [params] : [])] as const;
};

export const getListOpportunitiesQueryOptions = <
  TData = Awaited<ReturnType<typeof listOpportunities>>,
  TError = AxiosError<void>,
>(
  params?: ListOpportunitiesParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof listOpportunities>>,
      TError,
      TData
    >;
    axios?: AxiosRequestConfig;
  },
) => {
  const { query: queryOptions, axios: axiosOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getListOpportunitiesQueryKey(params);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof listOpportunities>>
  > = ({ signal }) => listOpportunities(params, { signal, ...axiosOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof listOpportunities>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type ListOpportunitiesQueryResult = NonNullable<
  Awaited<ReturnType<typeof listOpportunities>>
>;
export type ListOpportunitiesQueryError = AxiosError<void>;

export function useListOpportunities<
  TData = Awaited<ReturnType<typeof listOpportunities>>,
  TError = AxiosError<void>,
>(
  params?: ListOpportunitiesParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof listOpportunities>>,
      TError,
      TData
    >;
    axios?: AxiosRequestConfig;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getListOpportunitiesQueryOptions(params, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * **Required Permission(s):** ORG_OPP_CREATE
 */
export const createOpportunity = (
  createOpportunityDto: CreateOpportunityDto,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<OpportunityDto>> => {
  return axios.default.post(
    `/api/opportunities`,
    createOpportunityDto,
    options,
  );
};

export const getCreateOpportunityMutationOptions = <
  TError = AxiosError<ProblemDetails | void>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createOpportunity>>,
    TError,
    { data: CreateOpportunityDto },
    TContext
  >;
  axios?: AxiosRequestConfig;
}): UseMutationOptions<
  Awaited<ReturnType<typeof createOpportunity>>,
  TError,
  { data: CreateOpportunityDto },
  TContext
> => {
  const mutationKey = ["createOpportunity"];
  const { mutation: mutationOptions, axios: axiosOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, axios: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof createOpportunity>>,
    { data: CreateOpportunityDto }
  > = (props) => {
    const { data } = props ?? {};

    return createOpportunity(data, axiosOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type CreateOpportunityMutationResult = NonNullable<
  Awaited<ReturnType<typeof createOpportunity>>
>;
export type CreateOpportunityMutationBody = CreateOpportunityDto;
export type CreateOpportunityMutationError = AxiosError<ProblemDetails | void>;

export const useCreateOpportunity = <
  TError = AxiosError<ProblemDetails | void>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createOpportunity>>,
    TError,
    { data: CreateOpportunityDto },
    TContext
  >;
  axios?: AxiosRequestConfig;
}): UseMutationResult<
  Awaited<ReturnType<typeof createOpportunity>>,
  TError,
  { data: CreateOpportunityDto },
  TContext
> => {
  const mutationOptions = getCreateOpportunityMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * **Required Permission(s):** ORG_OPP_GET
 */
export const getOpportunityById = (
  id: string,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<OpportunityDto>> => {
  return axios.default.get(`/api/opportunities/${id}`, options);
};

export const getGetOpportunityByIdQueryKey = (id?: string) => {
  return [`/api/opportunities/${id}`] as const;
};

export const getGetOpportunityByIdQueryOptions = <
  TData = Awaited<ReturnType<typeof getOpportunityById>>,
  TError = AxiosError<void | ProblemDetails>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getOpportunityById>>,
      TError,
      TData
    >;
    axios?: AxiosRequestConfig;
  },
) => {
  const { query: queryOptions, axios: axiosOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetOpportunityByIdQueryKey(id);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getOpportunityById>>
  > = ({ signal }) => getOpportunityById(id, { signal, ...axiosOptions });

  return {
    queryKey,
    queryFn,
    enabled: !!id,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof getOpportunityById>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type GetOpportunityByIdQueryResult = NonNullable<
  Awaited<ReturnType<typeof getOpportunityById>>
>;
export type GetOpportunityByIdQueryError = AxiosError<void | ProblemDetails>;

export function useGetOpportunityById<
  TData = Awaited<ReturnType<typeof getOpportunityById>>,
  TError = AxiosError<void | ProblemDetails>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getOpportunityById>>,
      TError,
      TData
    >;
    axios?: AxiosRequestConfig;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getGetOpportunityByIdQueryOptions(id, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * **Required Permission(s):** ORG_OPP_UPDATE
 */
export const updateOpportunity = (
  id: string,
  updateOpportunityDto: UpdateOpportunityDto,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<OpportunityDto>> => {
  return axios.default.put(
    `/api/opportunities/${id}`,
    updateOpportunityDto,
    options,
  );
};

export const getUpdateOpportunityMutationOptions = <
  TError = AxiosError<void | ProblemDetails>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof updateOpportunity>>,
    TError,
    { id: string; data: UpdateOpportunityDto },
    TContext
  >;
  axios?: AxiosRequestConfig;
}): UseMutationOptions<
  Awaited<ReturnType<typeof updateOpportunity>>,
  TError,
  { id: string; data: UpdateOpportunityDto },
  TContext
> => {
  const mutationKey = ["updateOpportunity"];
  const { mutation: mutationOptions, axios: axiosOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, axios: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof updateOpportunity>>,
    { id: string; data: UpdateOpportunityDto }
  > = (props) => {
    const { id, data } = props ?? {};

    return updateOpportunity(id, data, axiosOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type UpdateOpportunityMutationResult = NonNullable<
  Awaited<ReturnType<typeof updateOpportunity>>
>;
export type UpdateOpportunityMutationBody = UpdateOpportunityDto;
export type UpdateOpportunityMutationError = AxiosError<void | ProblemDetails>;

export const useUpdateOpportunity = <
  TError = AxiosError<void | ProblemDetails>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof updateOpportunity>>,
    TError,
    { id: string; data: UpdateOpportunityDto },
    TContext
  >;
  axios?: AxiosRequestConfig;
}): UseMutationResult<
  Awaited<ReturnType<typeof updateOpportunity>>,
  TError,
  { id: string; data: UpdateOpportunityDto },
  TContext
> => {
  const mutationOptions = getUpdateOpportunityMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * **Required Permission(s):** ORG_OPP_UPDATE
 */
export const patchOpportunity = (
  id: string,
  patchOpportunityDto: PatchOpportunityDto,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<OpportunityDto>> => {
  return axios.default.patch(
    `/api/opportunities/${id}`,
    patchOpportunityDto,
    options,
  );
};

export const getPatchOpportunityMutationOptions = <
  TError = AxiosError<void | ProblemDetails>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof patchOpportunity>>,
    TError,
    { id: string; data: PatchOpportunityDto },
    TContext
  >;
  axios?: AxiosRequestConfig;
}): UseMutationOptions<
  Awaited<ReturnType<typeof patchOpportunity>>,
  TError,
  { id: string; data: PatchOpportunityDto },
  TContext
> => {
  const mutationKey = ["patchOpportunity"];
  const { mutation: mutationOptions, axios: axiosOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, axios: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof patchOpportunity>>,
    { id: string; data: PatchOpportunityDto }
  > = (props) => {
    const { id, data } = props ?? {};

    return patchOpportunity(id, data, axiosOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PatchOpportunityMutationResult = NonNullable<
  Awaited<ReturnType<typeof patchOpportunity>>
>;
export type PatchOpportunityMutationBody = PatchOpportunityDto;
export type PatchOpportunityMutationError = AxiosError<void | ProblemDetails>;

export const usePatchOpportunity = <
  TError = AxiosError<void | ProblemDetails>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof patchOpportunity>>,
    TError,
    { id: string; data: PatchOpportunityDto },
    TContext
  >;
  axios?: AxiosRequestConfig;
}): UseMutationResult<
  Awaited<ReturnType<typeof patchOpportunity>>,
  TError,
  { id: string; data: PatchOpportunityDto },
  TContext
> => {
  const mutationOptions = getPatchOpportunityMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * **Required Permission(s):** ORG_OPP_DELETE
 */
export const deleteOpportunity = (
  id: string,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<void>> => {
  return axios.default.delete(`/api/opportunities/${id}`, options);
};

export const getDeleteOpportunityMutationOptions = <
  TError = AxiosError<ProblemDetails | void>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteOpportunity>>,
    TError,
    { id: string },
    TContext
  >;
  axios?: AxiosRequestConfig;
}): UseMutationOptions<
  Awaited<ReturnType<typeof deleteOpportunity>>,
  TError,
  { id: string },
  TContext
> => {
  const mutationKey = ["deleteOpportunity"];
  const { mutation: mutationOptions, axios: axiosOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, axios: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof deleteOpportunity>>,
    { id: string }
  > = (props) => {
    const { id } = props ?? {};

    return deleteOpportunity(id, axiosOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type DeleteOpportunityMutationResult = NonNullable<
  Awaited<ReturnType<typeof deleteOpportunity>>
>;

export type DeleteOpportunityMutationError = AxiosError<ProblemDetails | void>;

export const useDeleteOpportunity = <
  TError = AxiosError<ProblemDetails | void>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteOpportunity>>,
    TError,
    { id: string },
    TContext
  >;
  axios?: AxiosRequestConfig;
}): UseMutationResult<
  Awaited<ReturnType<typeof deleteOpportunity>>,
  TError,
  { id: string },
  TContext
> => {
  const mutationOptions = getDeleteOpportunityMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * **Required Permission(s):** ORG_OPP_CREATE
 */
export const createOpportunityFromTemplate = (
  createOpportunityFromTemplateDto: CreateOpportunityFromTemplateDto,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<OpportunityDto>> => {
  return axios.default.post(
    `/api/opportunities/from-template`,
    createOpportunityFromTemplateDto,
    options,
  );
};

export const getCreateOpportunityFromTemplateMutationOptions = <
  TError = AxiosError<ProblemDetails | void>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createOpportunityFromTemplate>>,
    TError,
    { data: CreateOpportunityFromTemplateDto },
    TContext
  >;
  axios?: AxiosRequestConfig;
}): UseMutationOptions<
  Awaited<ReturnType<typeof createOpportunityFromTemplate>>,
  TError,
  { data: CreateOpportunityFromTemplateDto },
  TContext
> => {
  const mutationKey = ["createOpportunityFromTemplate"];
  const { mutation: mutationOptions, axios: axiosOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, axios: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof createOpportunityFromTemplate>>,
    { data: CreateOpportunityFromTemplateDto }
  > = (props) => {
    const { data } = props ?? {};

    return createOpportunityFromTemplate(data, axiosOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type CreateOpportunityFromTemplateMutationResult = NonNullable<
  Awaited<ReturnType<typeof createOpportunityFromTemplate>>
>;
export type CreateOpportunityFromTemplateMutationBody =
  CreateOpportunityFromTemplateDto;
export type CreateOpportunityFromTemplateMutationError =
  AxiosError<ProblemDetails | void>;

export const useCreateOpportunityFromTemplate = <
  TError = AxiosError<ProblemDetails | void>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createOpportunityFromTemplate>>,
    TError,
    { data: CreateOpportunityFromTemplateDto },
    TContext
  >;
  axios?: AxiosRequestConfig;
}): UseMutationResult<
  Awaited<ReturnType<typeof createOpportunityFromTemplate>>,
  TError,
  { data: CreateOpportunityFromTemplateDto },
  TContext
> => {
  const mutationOptions =
    getCreateOpportunityFromTemplateMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * **Required Permission(s):** ORG_OPP_PUBLISH
 */
export const shareOpportunity = (
  id: string,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<OpportunityDto>> => {
  return axios.default.post(
    `/api/opportunities/${id}/share`,
    undefined,
    options,
  );
};

export const getShareOpportunityMutationOptions = <
  TError = AxiosError<void | ProblemDetails>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof shareOpportunity>>,
    TError,
    { id: string },
    TContext
  >;
  axios?: AxiosRequestConfig;
}): UseMutationOptions<
  Awaited<ReturnType<typeof shareOpportunity>>,
  TError,
  { id: string },
  TContext
> => {
  const mutationKey = ["shareOpportunity"];
  const { mutation: mutationOptions, axios: axiosOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, axios: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof shareOpportunity>>,
    { id: string }
  > = (props) => {
    const { id } = props ?? {};

    return shareOpportunity(id, axiosOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type ShareOpportunityMutationResult = NonNullable<
  Awaited<ReturnType<typeof shareOpportunity>>
>;

export type ShareOpportunityMutationError = AxiosError<void | ProblemDetails>;

export const useShareOpportunity = <
  TError = AxiosError<void | ProblemDetails>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof shareOpportunity>>,
    TError,
    { id: string },
    TContext
  >;
  axios?: AxiosRequestConfig;
}): UseMutationResult<
  Awaited<ReturnType<typeof shareOpportunity>>,
  TError,
  { id: string },
  TContext
> => {
  const mutationOptions = getShareOpportunityMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * **Required Permission(s):** ORG_OPP_CLOSE
 */
export const closeOpportunity = (
  id: string,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<OpportunityDto>> => {
  return axios.default.post(
    `/api/opportunities/${id}/close`,
    undefined,
    options,
  );
};

export const getCloseOpportunityMutationOptions = <
  TError = AxiosError<ProblemDetails | void>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof closeOpportunity>>,
    TError,
    { id: string },
    TContext
  >;
  axios?: AxiosRequestConfig;
}): UseMutationOptions<
  Awaited<ReturnType<typeof closeOpportunity>>,
  TError,
  { id: string },
  TContext
> => {
  const mutationKey = ["closeOpportunity"];
  const { mutation: mutationOptions, axios: axiosOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, axios: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof closeOpportunity>>,
    { id: string }
  > = (props) => {
    const { id } = props ?? {};

    return closeOpportunity(id, axiosOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type CloseOpportunityMutationResult = NonNullable<
  Awaited<ReturnType<typeof closeOpportunity>>
>;

export type CloseOpportunityMutationError = AxiosError<ProblemDetails | void>;

export const useCloseOpportunity = <
  TError = AxiosError<ProblemDetails | void>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof closeOpportunity>>,
    TError,
    { id: string },
    TContext
  >;
  axios?: AxiosRequestConfig;
}): UseMutationResult<
  Awaited<ReturnType<typeof closeOpportunity>>,
  TError,
  { id: string },
  TContext
> => {
  const mutationOptions = getCloseOpportunityMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * **Required Permission(s):** ORG_OPP_SHARE
 */
export const inviteToOpportunity = (
  id: string,
  inviteTalentMembersDto: InviteTalentMembersDto,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<OpportunityDto>> => {
  return axios.default.post(
    `/api/opportunities/${id}/invite`,
    inviteTalentMembersDto,
    options,
  );
};

export const getInviteToOpportunityMutationOptions = <
  TError = AxiosError<void | ProblemDetails>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof inviteToOpportunity>>,
    TError,
    { id: string; data: InviteTalentMembersDto },
    TContext
  >;
  axios?: AxiosRequestConfig;
}): UseMutationOptions<
  Awaited<ReturnType<typeof inviteToOpportunity>>,
  TError,
  { id: string; data: InviteTalentMembersDto },
  TContext
> => {
  const mutationKey = ["inviteToOpportunity"];
  const { mutation: mutationOptions, axios: axiosOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, axios: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof inviteToOpportunity>>,
    { id: string; data: InviteTalentMembersDto }
  > = (props) => {
    const { id, data } = props ?? {};

    return inviteToOpportunity(id, data, axiosOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type InviteToOpportunityMutationResult = NonNullable<
  Awaited<ReturnType<typeof inviteToOpportunity>>
>;
export type InviteToOpportunityMutationBody = InviteTalentMembersDto;
export type InviteToOpportunityMutationError =
  AxiosError<void | ProblemDetails>;

export const useInviteToOpportunity = <
  TError = AxiosError<void | ProblemDetails>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof inviteToOpportunity>>,
    TError,
    { id: string; data: InviteTalentMembersDto },
    TContext
  >;
  axios?: AxiosRequestConfig;
}): UseMutationResult<
  Awaited<ReturnType<typeof inviteToOpportunity>>,
  TError,
  { id: string; data: InviteTalentMembersDto },
  TContext
> => {
  const mutationOptions = getInviteToOpportunityMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * **Required Permission(s):** ORG_OPP_SHARE
 */
export const removeFromOpportunity = (
  id: string,
  removeSharesDto: RemoveSharesDto,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<OpportunityDto>> => {
  return axios.default.delete(`/api/opportunities/${id}/invite`, {
    data: removeSharesDto,
    ...options,
  });
};

export const getRemoveFromOpportunityMutationOptions = <
  TError = AxiosError<void | ProblemDetails>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof removeFromOpportunity>>,
    TError,
    { id: string; data: RemoveSharesDto },
    TContext
  >;
  axios?: AxiosRequestConfig;
}): UseMutationOptions<
  Awaited<ReturnType<typeof removeFromOpportunity>>,
  TError,
  { id: string; data: RemoveSharesDto },
  TContext
> => {
  const mutationKey = ["removeFromOpportunity"];
  const { mutation: mutationOptions, axios: axiosOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, axios: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof removeFromOpportunity>>,
    { id: string; data: RemoveSharesDto }
  > = (props) => {
    const { id, data } = props ?? {};

    return removeFromOpportunity(id, data, axiosOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type RemoveFromOpportunityMutationResult = NonNullable<
  Awaited<ReturnType<typeof removeFromOpportunity>>
>;
export type RemoveFromOpportunityMutationBody = RemoveSharesDto;
export type RemoveFromOpportunityMutationError =
  AxiosError<void | ProblemDetails>;

export const useRemoveFromOpportunity = <
  TError = AxiosError<void | ProblemDetails>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof removeFromOpportunity>>,
    TError,
    { id: string; data: RemoveSharesDto },
    TContext
  >;
  axios?: AxiosRequestConfig;
}): UseMutationResult<
  Awaited<ReturnType<typeof removeFromOpportunity>>,
  TError,
  { id: string; data: RemoveSharesDto },
  TContext
> => {
  const mutationOptions = getRemoveFromOpportunityMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * **Required Permission(s):** ORG_OPP_GET
 */
export const getOpportunityShares = (
  id: string,
  params?: GetOpportunitySharesParams,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<OpportunityShareDtoPaginatedResult>> => {
  return axios.default.get(`/api/opportunities/${id}/shares`, {
    ...options,
    params: { ...params, ...options?.params },
  });
};

export const getGetOpportunitySharesQueryKey = (
  id?: string,
  params?: GetOpportunitySharesParams,
) => {
  return [
    `/api/opportunities/${id}/shares`,
    ...(params ? [params] : []),
  ] as const;
};

export const getGetOpportunitySharesQueryOptions = <
  TData = Awaited<ReturnType<typeof getOpportunityShares>>,
  TError = AxiosError<void | ProblemDetails>,
>(
  id: string,
  params?: GetOpportunitySharesParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getOpportunityShares>>,
      TError,
      TData
    >;
    axios?: AxiosRequestConfig;
  },
) => {
  const { query: queryOptions, axios: axiosOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetOpportunitySharesQueryKey(id, params);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getOpportunityShares>>
  > = ({ signal }) =>
    getOpportunityShares(id, params, { signal, ...axiosOptions });

  return {
    queryKey,
    queryFn,
    enabled: !!id,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof getOpportunityShares>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type GetOpportunitySharesQueryResult = NonNullable<
  Awaited<ReturnType<typeof getOpportunityShares>>
>;
export type GetOpportunitySharesQueryError = AxiosError<void | ProblemDetails>;

export function useGetOpportunityShares<
  TData = Awaited<ReturnType<typeof getOpportunityShares>>,
  TError = AxiosError<void | ProblemDetails>,
>(
  id: string,
  params?: GetOpportunitySharesParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getOpportunityShares>>,
      TError,
      TData
    >;
    axios?: AxiosRequestConfig;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getGetOpportunitySharesQueryOptions(id, params, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * **Required Permission(s):** ORG_OPP_LIST
 */
export const getOpportunityStats = (
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<OpportunityStatsDto>> => {
  return axios.default.get(`/api/opportunities/stats`, options);
};

export const getGetOpportunityStatsQueryKey = () => {
  return [`/api/opportunities/stats`] as const;
};

export const getGetOpportunityStatsQueryOptions = <
  TData = Awaited<ReturnType<typeof getOpportunityStats>>,
  TError = AxiosError<void>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof getOpportunityStats>>,
    TError,
    TData
  >;
  axios?: AxiosRequestConfig;
}) => {
  const { query: queryOptions, axios: axiosOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetOpportunityStatsQueryKey();

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getOpportunityStats>>
  > = ({ signal }) => getOpportunityStats({ signal, ...axiosOptions });

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getOpportunityStats>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type GetOpportunityStatsQueryResult = NonNullable<
  Awaited<ReturnType<typeof getOpportunityStats>>
>;
export type GetOpportunityStatsQueryError = AxiosError<void>;

export function useGetOpportunityStats<
  TData = Awaited<ReturnType<typeof getOpportunityStats>>,
  TError = AxiosError<void>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof getOpportunityStats>>,
    TError,
    TData
  >;
  axios?: AxiosRequestConfig;
}): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getGetOpportunityStatsQueryOptions(options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * **Required Permission(s):** ORG_CLAIM_LIST
 */
export const getOpportunityClaims = (
  id: string,
  params?: GetOpportunityClaimsParams,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<OpportunityClaimDtoPaginatedResult>> => {
  return axios.default.get(`/api/opportunities/${id}/claims`, {
    ...options,
    params: { ...params, ...options?.params },
  });
};

export const getGetOpportunityClaimsQueryKey = (
  id?: string,
  params?: GetOpportunityClaimsParams,
) => {
  return [
    `/api/opportunities/${id}/claims`,
    ...(params ? [params] : []),
  ] as const;
};

export const getGetOpportunityClaimsQueryOptions = <
  TData = Awaited<ReturnType<typeof getOpportunityClaims>>,
  TError = AxiosError<void>,
>(
  id: string,
  params?: GetOpportunityClaimsParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getOpportunityClaims>>,
      TError,
      TData
    >;
    axios?: AxiosRequestConfig;
  },
) => {
  const { query: queryOptions, axios: axiosOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetOpportunityClaimsQueryKey(id, params);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getOpportunityClaims>>
  > = ({ signal }) =>
    getOpportunityClaims(id, params, { signal, ...axiosOptions });

  return {
    queryKey,
    queryFn,
    enabled: !!id,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof getOpportunityClaims>>,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type GetOpportunityClaimsQueryResult = NonNullable<
  Awaited<ReturnType<typeof getOpportunityClaims>>
>;
export type GetOpportunityClaimsQueryError = AxiosError<void>;

export function useGetOpportunityClaims<
  TData = Awaited<ReturnType<typeof getOpportunityClaims>>,
  TError = AxiosError<void>,
>(
  id: string,
  params?: GetOpportunityClaimsParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getOpportunityClaims>>,
      TError,
      TData
    >;
    axios?: AxiosRequestConfig;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } {
  const queryOptions = getGetOpportunityClaimsQueryOptions(id, params, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}
